// Common utilities for React Query

import { type AxiosError } from 'axios'

// Error handling utilities
export interface ErrorUtils {
  transformAxiosError: (error: any) => string
  logErrorInDev: (error: any, context: string) => void
  isNetworkError: (error: AxiosError) => boolean
}

export const errorUtils: ErrorUtils = {
  transformAxiosError(error) {
    // Handle backend error structure: { data: { detail: "message" } }
    if (error?.response?.data?.data?.detail) {
      return error.response.data.data.detail
    }

    // Handle backend error structure: { data: { detail: "message" } }
    if (error?.response?.data?.detail) {
      return error.response.data.detail
    }

    // Handle array of errors
    if (error?.response?.data?.errors?.[0]?.message) {
      return error.response.data.errors[0].message
    }

    // Handle generic message
    if (error?.response?.data?.message) {
      return error.response.data.message
    }

    if (error?.response?.status) {
      switch (error.response?.status) {
        case 400:
          return 'Solicitud inválida. Verifique los datos enviados.'
        case 401:
          return 'No autorizado. Inicie sesión nuevamente.'
        case 403:
          return 'Acceso denegado. No tiene permisos para esta acción.'
        case 404:
          return 'Recurso no encontrado.'
        case 409:
          return 'Conflicto. El recurso ya existe o hay un problema de concurrencia.'
        case 422:
          return 'Datos de entrada inválidos. Verifique la información proporcionada.'
        case 500:
          return 'Error interno del servidor. Intente nuevamente más tarde.'
        case 503:
          return 'Servicio no disponible. Intente nuevamente más tarde.'
        // default:
        //   return error.message || 'Error de conexión. Verifique su conexión a internet.'
      }
    }

    // Handle axios error message
    if (error?.message) {
      return error.message
    }

    if (error instanceof Error) {
      return error.message
    }

    if (typeof error === 'string') {
      return error
    }

    return 'Ha ocurrido un error inesperado'
  },

  logErrorInDev(error, context) {
    if (import.meta.env.DEV) {
      console.error(`[${context}] Error:`, error)
    }
  },

  isNetworkError(error: AxiosError): boolean {
    return !error.response && (error.code === 'NETWORK_ERROR' || error.code === 'ECONNABORTED')
  }
}

// Default query options
export const defaultQueryOptions = {
  retry: 1,
  retryDelay: 1000,
  refetchOnWindowFocus: false,
  gcTime: 5 * 60 * 1000 // 5 minutes
} as const
